import Phaser from 'phaser';
import type { CompassDirection } from '@hexcrawl/shared-utils';

export type FacingDirection = CompassDirection;

type SyncOptions = {
  forcePlay?: boolean;
};

type NameTagOptions = {
  text: string;
  color?: string;
  fontSize?: string;
  yOffset?: number;
  depth?: number;
};

type StatBarColors = {
  bg: number;
  fill: number;
};

const DEFAULT_BAR_WIDTH: number = 80;
const DEFAULT_BAR_HEIGHT: number = 8;
const DEFAULT_BAR_SPACING: number = 4;
const DEFAULT_Y_OFFSET: number = -32;

// The buff tag sits immediately above the stat bars, out of the sprite's way.
const BUFF_TAG_GAP: number = 4;
const BUFF_TAG_FONT_SIZE: string = '20px';
const BUFF_TAG_COLOR: string = '#ffd27f';

type StatBarOptions = {
  maxHP: number;
  maxMP: number;
  currentHP: number;
  currentMP: number;
  barWidth?: number;
  barHeight?: number;
  barSpacing?: number;
  yOffset?: number;
  depth?: number;
  hpColors?: StatBarColors;
  mpColors?: StatBarColors;
};

type NameTagBundle = {
  text: Phaser.GameObjects.Text;
  yOffset: number;
};

type StatBarBundle = {
  container: Phaser.GameObjects.Container;
  hpFill: Phaser.GameObjects.Rectangle;
  mpFill: Phaser.GameObjects.Rectangle;
  barWidth: number;
  maxHP: number;
  maxMP: number;
  yOffset: number;
  lastHP: number;
  lastMP: number;
};

type BuffTagBundle = {
  text: Phaser.GameObjects.Text;
};

type AnimationWaiter = {
  key: string;
  resolve: () => void;
  fallback: Phaser.Time.TimerEvent | null;
  done: boolean;
};

export class AnimatedSprite {
  private _sprite: Phaser.GameObjects.Sprite;
  private _facing: FacingDirection;
  private _currentAnimationKey: string;
  private _defaultAnimationKey: string;
  private _namespace?: string;
  private _lastPlayedKey?: string;
  private _freezeOnComplete: boolean;
  private _nameTag?: NameTagBundle;
  private _statBars?: StatBarBundle;
  private _buffTag?: BuffTagBundle;
  private _animationWaiters: Set<AnimationWaiter>;

  constructor(
    sprite: Phaser.GameObjects.Sprite,
    namespace: string,
    facing: FacingDirection = 'south',
    animationKey: string = 'idle',
    defaultAnimationKey: string = 'idle'
  ) {
    this._sprite = sprite;
    this._facing = facing;
    this._currentAnimationKey = animationKey;
    this._defaultAnimationKey = defaultAnimationKey;
    this._namespace = namespace;
    this._freezeOnComplete = false;
    this._animationWaiters = new Set();
    this._sprite.on(Phaser.GameObjects.Events.DESTROY, this.onSpriteDestroy);
    this.syncAnimation({ forcePlay: true });
  }

  get x(): number {
    return this._sprite.x;
  }

  get y(): number {
    return this._sprite.y;
  }

  set x(x: number) {
    this._sprite.x = x;
    this.updateOverlayPositions();
  }

  set y(y: number) {
    this._sprite.y = y;
    this.updateOverlayPositions();
  }

  get sprite(): Phaser.GameObjects.Sprite {
    return this._sprite;
  }

  get facing(): FacingDirection {
    return this._facing;
  }

  get currentAnimationKey(): string {
    return this._currentAnimationKey;
  }

  get defaultAnimationKey(): string {
    return this._defaultAnimationKey;
  }

  setDefaultAnimation(key: string) {
    this._defaultAnimationKey = key;
  }

  setFacing(facing: FacingDirection) {
    if (this._facing === facing) return;
    this._facing = facing;
    this.syncAnimation();
  }

  playAnimation(key: string, options: { freezeAtEnd?: boolean } = {}): Promise<void> {
    const freezeAtEnd = options.freezeAtEnd ?? false;
    const keyChanged = this._currentAnimationKey !== key;
    const freezeChanged = this._freezeOnComplete !== freezeAtEnd;
    if (!keyChanged && !freezeChanged) {
      return this.waitForCurrentAnimation();
    }

    this._currentAnimationKey = key;
    this._freezeOnComplete = freezeAtEnd;
    this.syncAnimation({ forcePlay: keyChanged });

    const currentAnim = this._sprite.anims?.currentAnim;
    const resolvedKey = this._lastPlayedKey ?? '(none)';
    if (!currentAnim) {
      console.warn(`[AnimatedSprite] playAnimation('${key}'): sprite.play('${resolvedKey}') — currentAnim is null after play (animation key not registered?)`);
    } else {
      console.log(`[AnimatedSprite] playAnimation('${key}'): playing '${resolvedKey}', duration=${currentAnim.duration}ms, frames=${currentAnim.frames.length}`);
    }

    return this.waitForCurrentAnimation();
  }

  getTopY() {
    const frame = this._sprite.frame as any;
    const sourceHeight = frame?.sourceSize?.h ?? frame?.height ?? this._sprite.displayHeight;
    const spriteSourceY = frame?.spriteSourceSize?.y ?? 0;
    const scaleY = this._sprite.scaleY || 1;
    // sprite origin is bottom-center, so measure from bottom of source to top of visible content
    return this._sprite.y - (sourceHeight - spriteSourceY) * scaleY;
  }

  enableNameTag(options: NameTagOptions) {
    const yOffset = options.yOffset ?? this._nameTag?.yOffset ?? 5;
    if (!this._nameTag) {
      const nameTag = this._sprite.scene.add.text(
        this._sprite.x,
        this._sprite.y + yOffset,
        options.text,
        {
          fontSize: options.fontSize ?? '8px',
          color: options.color ?? '#ffffff',
        }
      ).setOrigin(0.5, 0);
      if (options.depth !== undefined) {
        nameTag.setDepth(options.depth);
      }
      this._nameTag = { text: nameTag, yOffset };
    } else {
      this._nameTag.yOffset = yOffset;
      this._nameTag.text.setText(options.text);
      if (options.color) {
        this._nameTag.text.setColor(options.color);
      }
      if (options.fontSize) {
        this._nameTag.text.setFontSize(options.fontSize);
      }
      if (options.depth !== undefined) {
        this._nameTag.text.setDepth(options.depth);
      }
    }
    this.updateOverlayPositions();
  }

  disableNameTag() {
    if (!this._nameTag) return;
    this._nameTag.text.destroy();
    this._nameTag = undefined;
  }

  enableStatBars(options: StatBarOptions) {
    const barWidth = options.barWidth ?? DEFAULT_BAR_WIDTH;
    const barHeight = options.barHeight ?? DEFAULT_BAR_HEIGHT;
    const barSpacing = options.barSpacing ?? DEFAULT_BAR_SPACING;
    const yOffset = options.yOffset ?? DEFAULT_Y_OFFSET;
    const hpColors = options.hpColors ?? { bg: 0x2b0f0f, fill: 0xd64a4a };
    const mpColors = options.mpColors ?? { bg: 0x0d1a2b, fill: 0x4a82d6 };

    if (this._statBars) {
      this._statBars.maxHP = options.maxHP;
      this._statBars.maxMP = options.maxMP;
      this._statBars.barWidth = barWidth;
      this._statBars.yOffset = yOffset;
      if (options.depth !== undefined) {
        this._statBars.container.setDepth(options.depth);
      }
      this.setVitals(options.currentHP, options.currentMP, { animate: false });
      this.updateOverlayPositions();
      return;
    }

    const maxHP = options.maxHP;
    const maxMP = options.maxMP;
    const hpRatio = maxHP > 0 ? Phaser.Math.Clamp(options.currentHP / maxHP, 0, 1) : 0;
    const mpRatio = maxMP > 0 ? Phaser.Math.Clamp(options.currentMP / maxMP, 0, 1) : 0;

    const container = this._sprite.scene.add.container();
    container.setPosition(this._sprite.x, this.getTopY() + yOffset);
    if (options.depth !== undefined) {
      container.setDepth(options.depth);
    }

    const hpBg = this._sprite.scene.add.rectangle(
      -barWidth / 2,
      0,
      barWidth,
      barHeight,
      hpColors.bg
    ).setOrigin(0, 0);
    const hpFill = this._sprite.scene.add.rectangle(
      -barWidth / 2,
      0,
      barWidth * hpRatio,
      barHeight,
      hpColors.fill
    ).setOrigin(0, 0);

    const mpBg = this._sprite.scene.add.rectangle(
      -barWidth / 2,
      barHeight + barSpacing,
      barWidth,
      barHeight,
      mpColors.bg
    ).setOrigin(0, 0);
    const mpFill = this._sprite.scene.add.rectangle(
      -barWidth / 2,
      barHeight + barSpacing,
      barWidth * mpRatio,
      barHeight,
      mpColors.fill
    ).setOrigin(0, 0);

    container.add([hpBg, hpFill, mpBg, mpFill]);
    this._statBars = {
      container,
      hpFill,
      mpFill,
      barWidth,
      maxHP,
      maxMP,
      yOffset,
      lastHP: options.currentHP,
      lastMP: options.currentMP,
    };
    this.updateOverlayPositions();
  }

  disableStatBars() {
    if (!this._statBars) return;
    this._statBars.container.destroy();
    this._statBars = undefined;
  }

  /**
   * Show a line of timed-effect text above the stat bars, or clear it with an empty
   * string. The text object is created lazily, so an entity that is never buffed pays
   * nothing for it.
   */
  setBuffTag(text: string) {
    if (!this._buffTag) {
      if (text.length === 0) return;
      const tag = this._sprite.scene.add.text(this._sprite.x, this._sprite.y, text, {
        fontSize: BUFF_TAG_FONT_SIZE,
        color: BUFF_TAG_COLOR,
      }).setOrigin(0.5, 1);
      tag.setDepth(this._sprite.depth + 1);
      this._buffTag = { text: tag };
      this.updateOverlayPositions();
      return;
    }

    if (this._buffTag.text.text !== text) {
      this._buffTag.text.setText(text);
      this.updateOverlayPositions();
    }
    this._buffTag.text.setVisible(text.length > 0);
  }

  setVitals(currentHP: number, currentMP: number, options: { animate?: boolean } = {}) {
    if (!this._statBars) return;
    const animate = options.animate ?? true;
    const prevHP = this._statBars.lastHP;
    const prevMP = this._statBars.lastMP;
    if (prevHP === currentHP && prevMP === currentMP) return;

    const hpDelta = currentHP - prevHP;
    const mpDelta = currentMP - prevMP;
    if (animate) {
      const showHP = hpDelta !== 0;
      const showMP = mpDelta !== 0;
      const xOffset = showHP && showMP ? 6 : 0;
      if (showHP) {
        const hpText = hpDelta > 0 ? `+${hpDelta}` : `${hpDelta}`;
        const hpColor = hpDelta > 0 ? '#65d670' : '#d65c5c';
        this.spawnFloatingNumber(hpText, hpColor, showMP ? -xOffset : 0);
      }
      if (showMP) {
        const mpText = mpDelta > 0 ? `+${mpDelta}` : `${mpDelta}`;
        this.spawnFloatingNumber(mpText, '#4a82d6', showHP ? xOffset : 0);
      }
    }

    if (prevHP !== currentHP) {
      const hpRatio = this._statBars.maxHP > 0
        ? Phaser.Math.Clamp(currentHP / this._statBars.maxHP, 0, 1)
        : 0;
      if (animate) {
        this._sprite.scene.tweens.add({
          targets: this._statBars.hpFill,
          width: this._statBars.barWidth * hpRatio,
          duration: 250,
          ease: 'Sine.easeOut',
        });
      } else {
        this._statBars.hpFill.width = this._statBars.barWidth * hpRatio;
      }
    }

    if (prevMP !== currentMP) {
      const mpRatio = this._statBars.maxMP > 0
        ? Phaser.Math.Clamp(currentMP / this._statBars.maxMP, 0, 1)
        : 0;
      if (animate) {
        this._sprite.scene.tweens.add({
          targets: this._statBars.mpFill,
          width: this._statBars.barWidth * mpRatio,
          duration: 250,
          ease: 'Sine.easeOut',
        });
      } else {
        this._statBars.mpFill.width = this._statBars.barWidth * mpRatio;
      }
    }

    this._statBars.lastHP = currentHP;
    this._statBars.lastMP = currentMP;
  }

  updateOverlayPositions() {
    if (this._nameTag) {
      this._nameTag.text.setPosition(this._sprite.x, this._sprite.y + this._nameTag.yOffset);
    }
    if (this._statBars) {
      this._statBars.container.setPosition(this._sprite.x, this.getTopY() + this._statBars.yOffset);
    }
    if (this._buffTag) {
      const barsTop = this.getTopY() + (this._statBars?.yOffset ?? DEFAULT_Y_OFFSET);
      this._buffTag.text.setPosition(this._sprite.x, barsTop - BUFF_TAG_GAP);
    }
  }

  setDepth(baseDepth: number, overlayOffset: number = 1) {
    this._sprite.setDepth(baseDepth);
    if (this._nameTag) {
      this._nameTag.text.setDepth(baseDepth + overlayOffset);
    }
    if (this._statBars) {
      this._statBars.container.setDepth(baseDepth + overlayOffset);
    }
    if (this._buffTag) {
      this._buffTag.text.setDepth(baseDepth + overlayOffset);
    }
  }

  waitForAnimationComplete(): Promise<void> {
    return this.waitForCurrentAnimation();
  }

  destroy() {
    this.disableNameTag();
    this.disableStatBars();
    this._buffTag?.text.destroy();
    this._buffTag = undefined;
    this.resolveAllAnimationWaiters();
    this._sprite.destroy();
  }

  private syncAnimation(options: SyncOptions = {}) {
    const animKey = this.formatAnimationKey(`${this._currentAnimationKey}_${this._facing}`);
    if (options.forcePlay || this._lastPlayedKey !== animKey) {
      this._lastPlayedKey = animKey;
      this._sprite.play(animKey);
    }

    this._sprite.off(
      Phaser.Animations.Events.ANIMATION_COMPLETE,
      this.onAnimationComplete
    );
    this._sprite.on(
      Phaser.Animations.Events.ANIMATION_COMPLETE,
      this.onAnimationComplete
    );
  }

  private formatAnimationKey(key: string): string {
    return this._namespace ? `${this._namespace}:${key}` : key;
  }

  private spawnFloatingNumber(text: string, color: string, xOffset: number = 0) {
    const startX = this._sprite.x + xOffset;
    const startY = this.getTopY() - 4;
    const label = this._sprite.scene.add.text(startX, startY, text, {
      fontSize: '9px',
      color,
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5, 1);

    const baseDepth = this._statBars?.container.depth ?? this._sprite.depth;
    label.setDepth(baseDepth + 5);

    this._sprite.scene.tweens.add({
      targets: label,
      y: startY - 14,
      alpha: 0,
      duration: 650,
      ease: 'Sine.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  private onAnimationComplete = (animation: Phaser.Animations.Animation) => {
    this.resolveAnimationWaiters(animation.key);
    if (this._freezeOnComplete) return;
    if (animation.key !== this._lastPlayedKey) {
      return;
    }

    if (this._currentAnimationKey === this._defaultAnimationKey) return;
    this.playAnimation(this._defaultAnimationKey);
  };

  private waitForCurrentAnimation(): Promise<void> {
    const currentAnim = this._sprite.anims?.currentAnim;
    if (!currentAnim) {
      return Promise.resolve();
    }
    return this.waitForAnimationKey(currentAnim.key, currentAnim.duration ?? 0);
  }

  private waitForAnimationKey(expectedKey: string, duration: number): Promise<void> {
    return new Promise((resolve) => {
      if (!expectedKey) {
        resolve();
        return;
      }

      const waiter: AnimationWaiter = {
        key: expectedKey,
        resolve: () => {},
        fallback: null,
        done: false,
      };

      const finish = () => {
        if (waiter.done) return;
        waiter.done = true;
        if (waiter.fallback) {
          waiter.fallback.remove(false);
          waiter.fallback = null;
        }
        this._animationWaiters.delete(waiter);
        resolve();
      };

      waiter.resolve = finish;
      this._animationWaiters.add(waiter);

      const fallbackDelay = Math.max(duration, 0) + 50;
      if (fallbackDelay > 0 && this._sprite.scene?.time) {
        waiter.fallback = this._sprite.scene.time.delayedCall(fallbackDelay, finish);
      } else if (fallbackDelay > 0) {
        Promise.resolve().then(finish);
      }
    });
  }

  private resolveAnimationWaiters(animationKey: string) {
    if (this._animationWaiters.size === 0) return;
    for (const waiter of Array.from(this._animationWaiters)) {
      if (waiter.key === animationKey) {
        waiter.resolve();
      }
    }
  }

  private resolveAllAnimationWaiters() {
    if (this._animationWaiters.size === 0) return;
    for (const waiter of Array.from(this._animationWaiters)) {
      waiter.resolve();
    }
  }

  private onSpriteDestroy = () => {
    this.resolveAllAnimationWaiters();
  };
}
